import React from "react";
import { Box, Stack, Typography, Button, Card, CardContent } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { getTierDisplayName } from "../../utils/subscriptionUtils";
import NewColors from "../../themes/NewColors";
import UpgradeIcon from "@mui/icons-material/Upgrade";

const UpgradePrompt = ({
  organizationData,
  title = "Upgrade to unlock this feature",
  description,
  minTier = "standard",
  featureName,
  variant = "card", // "card", "banner", or "header"
}) => {
  const navigate = useNavigate();
  // Directly access account.accountType
  const currentTier = organizationData?.account?.accountType || "free";

  // Don't show if already at required tier or higher
  const tierLevels = {
    free: 0,
    standard: 1,
    pro: 2,
    special_pricing: 3,
  };

  const currentLevel = tierLevels[currentTier] || 0;
  const requiredLevel = tierLevels[minTier] || 0;

  if (currentLevel >= requiredLevel) {
    return null;
  }

  const handleUpgrade = () => {
    navigate("/subscribe");
  };

  const getRecommendedTier = () => {
    if (minTier === "pro") return "Pro";
    return "Standard";
  };

  if (variant === "header") {
    return (
      <Box
        sx={{
          backgroundColor: NewColors.brand.primary,
          color: "white",
          px: 2,
          py: 0.75,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontSize: "0.875rem",
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {title}
        </Typography>
        <Button
          size="small"
          variant="contained"
          onClick={handleUpgrade}
          sx={{
            backgroundColor: "white",
            color: NewColors.brand.primary,
            minWidth: "auto",
            px: 2,
            py: 0.5,
            fontSize: "0.75rem",
            fontWeight: 600,
            textTransform: "none",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.9)",
            },
          }}
        >
          Upgrade
        </Button>
      </Box>
    );
  }

  if (variant === "banner") {
    return (
      <Box
        sx={{
          backgroundColor: NewColors.brand.primary,
          color: "white",
          padding: 2,
          borderRadius: 1,
          marginBottom: 2,
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
          <Stack spacing={0.5}>
            <Typography variant="h6" fontWeight="bold">
              {title}
            </Typography>
            {description && (
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {description}
              </Typography>
            )}
          </Stack>
          <Button
            variant="contained"
            onClick={handleUpgrade}
            sx={{
              backgroundColor: "white",
              color: NewColors.brand.primary,
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.9)",
              },
            }}
          >
            Upgrade to {getRecommendedTier()}
          </Button>
        </Stack>
      </Box>
    );
  }

  // Default card variant
  return (
    <Card
      sx={{
        border: `1px solid ${NewColors.border.medium}`,
        borderRadius: 2,
        marginBottom: 2,
      }}
    >
      <CardContent>
        <Stack spacing={2} alignItems="center" textAlign="center">
          <UpgradeIcon
            sx={{
              fontSize: 48,
              color: NewColors.brand.primary,
            }}
          />
          <Typography variant="h6" fontWeight="bold">
            {title}
          </Typography>
          {description && (
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary">
            Your current plan: <strong>{getTierDisplayName(currentTier)}</strong>
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleUpgrade}
            size="large"
            sx={{
              marginTop: 1,
            }}
          >
            Upgrade to {getRecommendedTier()}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default UpgradePrompt;
